import { useEffect, useState } from 'react';
import { Auth, Hub } from '../services/amplifyCompat';
import { getSimpleUserFromAttributes } from '../utils/get-simple-user-from-attributes';

// Amplify v6 Hub auth events: 'signedIn' / 'signedOut' (v5 used 'signIn' / 'signOut')
export const USER_AUTH_EVENTS = {
  SIGN_IN: 'signedIn',
  SIGN_OUT: 'signedOut',
  // Keep legacy names for any code still checking v5 events
  SIGN_IN_LEGACY: 'signIn',
  SIGN_OUT_LEGACY: 'signOut',
};

export const useGetCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(undefined);

  useEffect(() => {
    const refreshAuthenticatedUser = async () => {
      try {
        const { attributes } = await Auth.currentAuthenticatedUser();
        const currentUserObject = getSimpleUserFromAttributes(attributes);
        setCurrentUser(currentUserObject);
        setIsUserLoggedIn(true);
      } catch {
        setCurrentUser(undefined);
        setIsUserLoggedIn(false);
      }
    };

    // Naming the returned function from Hub.listen allows us to
    // cancel the listener once we're finished with it:
    // https://docs.amplify.aws/javascript/build-a-backend/utilities/hub/#stop-listening
    const hubAuthListener = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (
        event === USER_AUTH_EVENTS.SIGN_IN ||
        event === USER_AUTH_EVENTS.SIGN_IN_LEGACY
      ) {
        // v6 payload no longer includes data.attributes — re-fetch the user
        refreshAuthenticatedUser();
      }
      if (
        event === USER_AUTH_EVENTS.SIGN_OUT ||
        event === USER_AUTH_EVENTS.SIGN_OUT_LEGACY
      ) {
        setCurrentUser(undefined);
        setIsUserLoggedIn(false);
      }
    });

    return () => {
      hubAuthListener();
    };
  }, []);

  useEffect(() => {
    const getAuthenticatedUser = async () => {
      try {
        const { attributes } = await Auth.currentAuthenticatedUser();
        const currentUserObject = getSimpleUserFromAttributes(attributes);
        setCurrentUser(currentUserObject);
        setIsUserLoggedIn(true);
      } catch {
        setCurrentUser(undefined);
        setIsUserLoggedIn(false);
      }
    };

    // Ease the Auth request frequency by only checking user status
    // when we've got an undefined (or unknown) login status
    if (isUserLoggedIn === undefined) {
      getAuthenticatedUser();
    }
  }, [isUserLoggedIn]);

  return {
    currentUser,
    isUserLoggedIn,
    isUserAdmin: !!(isUserLoggedIn && currentUser?.isAdmin),
  };
};
